import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { controlFocus } from '../primitives/shared.js';

export type BannerTone = 'info' | 'caution' | 'negative' | 'offline' | 'impersonation';

export interface BannerProps {
  /** Names the region — 07-11 PRM-28 requires `region` with an accessible name. */
  label: string;
  tone?: BannerTone;
  dismissible?: boolean;
  onDismiss?: () => void;
  children: ReactNode;
  className?: string;
}

const TONES: Record<BannerTone, string> = {
  info: 'border-line bg-sunken text-default',
  caution: 'border-late bg-sunken text-caution',
  negative: 'border-blocked bg-sunken text-negative',
  offline: 'border-late bg-sunken text-caution',
  impersonation: 'border-blocked bg-blocked text-inverse',
};

/**
 * PRM-28 Banner — a persistent contextual message. Assertive only for offline
 * and impersonation; those two resolve the `z.impersonation` band so that no
 * other surface, including a dialog, can occlude them (07-11).
 */
export function Banner({
  label,
  tone = 'info',
  dismissible,
  onDismiss,
  children,
  className,
}: BannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const assertive = tone === 'offline' || tone === 'impersonation';
  const elevated = assertive;

  if (dismissed) return null;

  return (
    <div
      role="region"
      aria-label={label}
      aria-live={assertive ? 'assertive' : undefined}
      data-primitive="PRM-28"
      tabIndex={0}
      className={cn(
        'flex items-center gap-inline border-l-4 px-field py-inline text-body',
        TONES[tone],
        elevated && 'sticky top-0 z-[var(--z-impersonation)]',
        controlFocus,
        className,
      )}
    >
      <span className="flex-1">{children}</span>
      {dismissible ? (
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          aria-label={`Dismiss ${label}`}
          className={cn('font-medium underline-offset-2', 'hov:underline', controlFocus)}
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );
}
